import { useEffect, useState } from 'react';

/**
 * Una foto que muestra su lugar mientras llega.
 *
 * Hay DOS esperas encadenadas y hasta ahora sólo se cubría la primera. Primero se pide la URL
 * firmada al servidor —eso ya tenía su esqueleto—; después el navegador descarga la imagen desde
 * el almacenamiento, y esa segunda espera es la larga: son cientos de kilobytes contra un objeto
 * remoto, con la red del teléfono. En ese tramo quedaba un hueco en blanco del tamaño exacto de la
 * foto, que se lee como un error de carga y no como algo en camino.
 *
 * El truco es no montar el `img` a medias: se dibuja igual, pero tapado por el esqueleto hasta que
 * dispara `load`. Así el navegador arranca la descarga enseguida —que es lo que importa— y la
 * persona ve movimiento mientras tanto.
 *
 * Si la imagen falla, el esqueleto se apaga igual. Un esqueleto que late para siempre promete algo
 * que no va a llegar, y eso es peor que un hueco.
 */
export function FotoConEspera({
  url,
  alt,
  className,
  onError,
}: {
  /** Null mientras todavía se está pidiendo la URL firmada. */
  url: string | null;
  alt: string;
  className?: string;
  onError?: () => void;
}) {
  const [cargada, setCargada] = useState(false);
  const [fallo, setFallo] = useState(false);

  // Cambiar de foto vuelve a empezar: sin esto, la nueva se mostraría de golpe con el estado de
  // «ya cargada» que dejó la anterior.
  useEffect(() => {
    setCargada(false);
    setFallo(false);
  }, [url]);

  const esperando = url === null || (!cargada && !fallo);

  return (
    <span className={className === undefined ? 'foto-espera' : `foto-espera ${className}`}>
      {url !== null && (
        <img
          src={url}
          alt={alt}
          className={cargada ? 'appear' : undefined}
          style={{ opacity: cargada ? 1 : 0 }}
          onLoad={() => setCargada(true)}
          onError={() => {
            setFallo(true);
            onError?.();
          }}
        />
      )}

      {esperando && <span className="skeleton foto-espera-hueco" aria-hidden="true" />}
    </span>
  );
}
